// Assemble and persist each team's season report card.
//
// Runs once per season, at the draft-phase transition — the earliest point
// where BOTH end-of-season data (final standings, playoff results, awards,
// end-of-season player ratings, the apron/tax ledger) AND the draft lottery
// order are final. For each team it builds a ReportCardInput from the various
// stores, scores it with the pure engine (common/seasonReportCard.ts), and
// stores the result on TeamSeason.reportCard.

import { idb } from "../../db/index.ts";
import { g } from "../../util/index.ts";
import {
	computeReportCard,
	type ReportCardAward,
	type ReportCardInput,
} from "../../../common/seasonReportCard.ts";

// A player this age or younger at season end counts toward "development".
export const DEV_AGE_MAX = 24;

// Minimal shapes the pure assembler needs (structurally satisfied by the real
// Player / TeamSeason objects).
type ApronLedger = {
	paidTax: boolean;
	overFirstApron: boolean;
	overSecondApron: boolean;
};
type PlayerForDev = {
	born: { year: number };
	ratings: readonly { season: number; ovr: number }[];
	draft: { ovr: number };
};

// Pure: turn already-loaded data into the engine's input. No I/O, so this is
// unit-tested directly (see reportCards.test.ts).
export const buildReportCardInput = (args: {
	season: number;
	numPlayoffRounds: number;
	won: number;
	lost: number;
	avgMargin: number;
	playoffRoundsWon: number;
	madePlayIn: boolean;
	projectedWins: number | undefined;
	players: PlayerForDev[];
	awards: ReportCardAward[];
	lotteryPick: number | null;
	apron: ApronLedger | undefined;
	priorApron: ApronLedger | undefined;
	payrollEndOfSeason: number | undefined;
	luxuryPayroll: number;
}): ReportCardInput => {
	// Young-player development: this season's ovr vs last season's (or the
	// draft-day ovr for a first-year player), for every young player.
	const youngPlayerOvrDeltas: number[] = [];
	for (const p of args.players) {
		const age = args.season - p.born.year;
		if (age > DEV_AGE_MAX) {
			continue;
		}
		const current = p.ratings.find((r) => r.season === args.season)?.ovr;
		if (current === undefined) {
			continue;
		}
		const prior = p.ratings.find((r) => r.season === args.season - 1)?.ovr;
		const baseline = prior ?? p.draft.ovr;
		youngPlayerOvrDeltas.push(current - baseline);
	}

	// Under the tax: prefer the apron ledger; fall back to comparing payroll to
	// the luxury-tax line for leagues without the apron ledger populated.
	const underTax = args.apron
		? args.apron.paidTax === false
		: args.payrollEndOfSeason !== undefined
			? args.payrollEndOfSeason < args.luxuryPayroll
			: false;

	return {
		numPlayoffRounds: args.numPlayoffRounds,
		won: args.won,
		lost: args.lost,
		avgMargin: args.avgMargin,
		playoffRoundsWon: args.playoffRoundsWon,
		madePlayIn: args.madePlayIn,
		projectedWins: args.projectedWins ?? args.won,
		youngPlayerOvrDeltas,
		awards: args.awards,
		lotteryPick: args.lotteryPick,
		underTax,
		paidTaxLastSeason: args.priorApron?.paidTax === true,
		escapedApron:
			args.priorApron?.overFirstApron === true &&
			args.apron?.overFirstApron === false,
	};
};

// Map the season's awards (and All-Stars) to a list of ReportCardAward per team.
const buildAwardsByTeam = async (
	season: number,
): Promise<Map<number, ReportCardAward[]>> => {
	const byTeam = new Map<number, ReportCardAward[]>();
	const add = (tid: number | undefined | null, award: ReportCardAward) => {
		if (typeof tid !== "number" || tid < 0) {
			return;
		}
		const list = byTeam.get(tid) ?? [];
		list.push(award);
		byTeam.set(tid, list);
	};

	// awards store: one object per season, loosely typed (per-sport union).
	const awards = await idb.getCopy.awards({ season }, "noCopyCache");
	if (awards) {
		const singles: [string, ReportCardAward][] = [
			["mvp", "mvp"],
			["roy", "roy"],
			["dpoy", "dpoy"],
			["smoy", "smoy"],
			["mip", "mip"],
			["finalsMvp", "finalsMvp"],
		];
		for (const [key, award] of singles) {
			add(awards[key]?.tid, award);
		}

		const allLeagueTiers: ReportCardAward[] = [
			"allLeague1",
			"allLeague2",
			"allLeague3",
		];
		if (Array.isArray(awards.allLeague)) {
			awards.allLeague.forEach((tier: any, i: number) => {
				const award = allLeagueTiers[i];
				if (!award || !tier?.players) {
					return;
				}
				for (const p of tier.players) {
					add(p?.tid, award);
				}
			});
		}

		if (Array.isArray(awards.allDefensive)) {
			for (const tier of awards.allDefensive) {
				for (const p of tier?.players ?? []) {
					add(p?.tid, "allDefensive");
				}
			}
		}
	}

	// All-Stars live in their own store.
	const allStars = await idb.getCopy.allStars({ season }, "noCopyCache");
	if (allStars) {
		for (const teamPlayers of allStars.teams) {
			for (const p of teamPlayers) {
				add(p?.tid, "allStar");
			}
		}
	}

	return byTeam;
};

// The best (lowest-numbered) lottery pick each team currently owns. Also used
// by the Executive Challenge finalize hook to record the "draft slot" input.
export const buildLotteryPickByTeam = async (
	season: number,
): Promise<Map<number, number>> => {
	const byTeam = new Map<number, number>();
	const lottery = await idb.getCopy.draftLotteryResults(
		{ season },
		"noCopyCache",
	);
	if (lottery) {
		for (const entry of lottery.result) {
			if (typeof entry.pick !== "number") {
				continue;
			}
			const existing = byTeam.get(entry.tid);
			if (existing === undefined || entry.pick < existing) {
				byTeam.set(entry.tid, entry.pick);
			}
		}
	}
	return byTeam;
};

// The set of tids that reached the play-in tournament this season.
const buildPlayInTids = async (season: number): Promise<Set<number>> => {
	const tids = new Set<number>();
	const playoffSeries = await idb.getCopy.playoffSeries(
		{ season },
		"noCopyCache",
	);
	for (const round of playoffSeries?.playIns ?? []) {
		for (const matchup of round) {
			tids.add(matchup.home.tid);
			tids.add(matchup.away.tid);
		}
	}
	return tids;
};

export const computeSeasonReportCards = async (season: number) => {
	const numPlayoffRounds = g.get("numGamesPlayoffSeries", "current").length;

	const teamSeasons = await idb.cache.teamSeasons.indexGetAll(
		"teamSeasonsBySeasonTid",
		[[season], [season, "Z"]],
	);
	const priorTeamSeasons = await idb.cache.teamSeasons.indexGetAll(
		"teamSeasonsBySeasonTid",
		[[season - 1], [season - 1, "Z"]],
	);
	const priorByTid = new Map(priorTeamSeasons.map((ts) => [ts.tid, ts]));

	// Regular-season point differential per game (mov) for each team.
	const teamsForMov = await idb.getCopies.teamsPlus(
		{
			attrs: ["tid"],
			stats: ["mov"],
			season,
		},
		"noCopyCache",
	);
	const movByTid = new Map(teamsForMov.map((t) => [t.tid, t.stats.mov ?? 0]));

	const [awardsByTeam, lotteryPickByTeam, playInTids] = await Promise.all([
		buildAwardsByTeam(season),
		buildLotteryPickByTeam(season),
		buildPlayInTids(season),
	]);

	const luxuryPayroll = g.get("luxuryPayroll");

	for (const ts of teamSeasons) {
		const tid = ts.tid;
		const players = await idb.cache.players.indexGetAll("playersByTid", tid);
		const priorTs = priorByTid.get(tid);

		const input = buildReportCardInput({
			season,
			numPlayoffRounds,
			won: ts.won,
			lost: ts.lost,
			avgMargin: movByTid.get(tid) ?? 0,
			playoffRoundsWon: ts.playoffRoundsWon,
			madePlayIn: playInTids.has(tid) && ts.playoffRoundsWon < 0,
			projectedWins: ts.projectedWins,
			players,
			awards: awardsByTeam.get(tid) ?? [],
			lotteryPick: lotteryPickByTeam.get(tid) ?? null,
			apron: ts.apron,
			priorApron: priorTs?.apron,
			payrollEndOfSeason: ts.payrollEndOfSeason,
			luxuryPayroll,
		});

		ts.reportCard = computeReportCard(input);
		await idb.cache.teamSeasons.put(ts);
	}
};

export default computeSeasonReportCards;
